import vr from "../../../utilFunctions/validationRules";
import { validateEmail } from "../../config/common/commonFunctions";
import { PhoneNumberUtil } from "google-libphonenumber";
import ACCOUNT_TYPES from "lodash/collection";
import {isPositiveNumber} from "../Customers/customersValidationRules";

const { lenBetween, isIdOrRelation, isNonNegativeInt, isValidUrl,isISO4217 } = vr;

const phoneUtil = PhoneNumberUtil.getInstance();
export const isPhoneValid = (phone) => {
    try {
        return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
    } catch (error) {
        return false;
    }
};

export const isEmpty = (v) =>
    v == null || (typeof v === "string" && v.trim() === "");

export const computeValidation = (value, rule, requiredFallback = "Required") => {
    const empty = isEmpty(value);
    const passes = rule.pattern ? rule.pattern(value) : true;

    const error = rule.required
        ? empty || (!empty && !passes)
        : !empty && !passes;

    const message =
        empty && rule.required
            ? rule.requiredMessage ?? requiredFallback
            : !passes
                ? rule.invalidMessage ?? rule.message ?? "Invalid value"
                : "";

    return { error, message, skipped: true };
};

export const vKeyOf = (name) => (name === "Industry" ? "industry" : name);

export const buildValidation = (t) => ({
    name: {
        required: true,
        requiredMessage: t.common.required_field,
        invalidMessage: t?.common?.minmax_chars?.replace?.("{min}", "3")?.replace?.("{max}", "150"),
        pattern: (v) => lenBetween(v, 3, 150),
        error: false,
        skipped: false,
    },
    phone: {
        required: true,
        requiredMessage: t.common.required_field,
        invalidMessage: t.customers.form.invalid_phone,
        pattern: (phone) => isPhoneValid(phone),
        error: false,
        skipped: false,
    },
    email: {
        required: true,
        requiredMessage: t.common.required_field,
        invalidMessage: t.customers.form.invalid_email,
        pattern: (email) => lenBetween(email, 6, 254) && validateEmail(email),
        error: false,
        skipped: false,
    },
    industry: {
        required: true,
        requiredMessage: t.common.required_field,
        invalidMessage: t.common.required_field,
        pattern: (v) => v != null && String(v).trim() !== "",
        error: false,
        skipped: false,
    },
    location: {
        required: true,
        requiredMessage: t.common.required_field,
        invalidMessage: t.common.required_field,
        pattern: (id) => isIdOrRelation(id),
        error: false,
        skipped: false,
    },
    preferences: {
        required: true,
        requiredMessage: t.common.required_field,
        invalidMessage: t.common.required_field,
        pattern: (v) => ["English", "العربية"].includes(v),
        error: false,
        skipped: false,
    },
    credit_days: {
        required: true,
        requiredMessage: t.common.required_field,
        invalidMessage: t.customers.form.invalid_credit_days,
        pattern: (v) => isNonNegativeInt(v),
        error: false,
        skipped: false,
    },

    website: {
        required: false,
        invalidMessage: t.customers.form.invalid_website,
        pattern: (website) =>
            website === "" || (lenBetween(website, 0, 255) && isValidUrl(website)),
        error: false,
        skipped: false,
    },
    serviceType: {
        required: false,
        invalidMessage:
            t.customers.form.invalid_service_type,
        pattern: (v) => v === "" || lenBetween(v, 3, 150),
        error: false,
        skipped: false,
    },
    description: {
        required: false,
        invalidMessage:
            t.customers.form.invalid_description,
        pattern: (v) => v === "" || lenBetween(v, 3, 255),
        error: false,
        skipped: false,
    },
    address: {
        required: false,
        invalidMessage: t.customers.form.invalid_address ,
        pattern: (v) => v === "" || lenBetween(v, 3, 250),
        error: false,
        skipped: false,
    },
    contact_name: {
        required: false,
        invalidMessage:
            t.customers.form.invalid_contact_name,
        pattern: (v) => v === "" || lenBetween(v, 3, 150),
        error: false,
        skipped: false,
    },
    contact_phone: {
        required: false,
        invalidMessage: t.customers.form.invalid_phone,
        pattern: (v) => v === "" || isPhoneValid(v),
        error: false,
        skipped: false,
    },
    contact_email: {
        required: false,
        invalidMessage: t.customers.form.invalid_email,
        pattern: (v) => v === "" || (lenBetween(v, 6, 254) && validateEmail(v)),
        error: false,
        skipped: false,
    },
    billing_name: {
        required: false,
        invalidMessage: t.customers.form.invalid_billing_name,
        pattern: (v) => v === "" || lenBetween(v, 3, 150),
        error: false,
        skipped: false,
    },
    billing_address: {
        required: false,
        invalidMessage: t.customers.form.invalid_address,
        pattern: (v) => v === "" || lenBetween(v, 3, 250),
        error: false,
        skipped: false,
    },
    billing_addressLine2: {
        required: false,
        invalidMessage: t.customers.form.invalid_addressLine,
        pattern: (v) => v === "" || lenBetween(v, 0, 250),
        error: false,
        skipped: false,
    },
    billing_addressLine3: {
        required: false,
        invalidMessage: t.customers.form.invalid_addressLine,
        pattern: (v) => v === "" || lenBetween(v, 0, 250),
        error: false,
        skipped: false,
    },
    billing_postal_code: {
        required: false,
        invalidMessage: t.customers.form.invalid_postal_code,
        pattern: (v) => v === "" || lenBetween(v, 1, 20),
        error: false,
        skipped: false,
    },
    billing_rate: {
        required: false,
        invalidMessage: t.customers.form.invalid_rate ,
        pattern: (v) => isEmpty(v) || Number(v) === 0 || isPositiveNumber(v),
        error: false,
        skipped: false,
    },
    bank: {
        required: false,
        invalidMessage: t.vendors.form.invalid_bank,
        pattern: (v) => v === "" || lenBetween(v, 0, 100),
        error: false,
        skipped: false,
    },

    account_type: {
        required: false,
        invalidMessage: t.common.invalid_value ,
        pattern: (v) => v === "" || ACCOUNT_TYPES.includes(String(v)),
        error: false,
        skipped: false,
    },

    account_no: {
        required: false,
        invalidMessage: t.vendors.form.invalid_account_no,
        pattern: (v) => v === "" || lenBetween(v, 0, 50),
        error: false,
        skipped: false,
    },

    IBAN: {
        required: false,
        invalidMessage: t.vendors.form.invalid_iban,
        pattern: (v) => v === "" || lenBetween(v, 0, 50),
        error: false,
        skipped: false,
    },

    disbursement_currency: {
        required: false,
        invalidMessage: t.common.invalid_value,
        pattern: (v) => isISO4217(v),
        error: false,
        skipped: false,
    },
});

export const makeOnBlurFactory = ({ getCustomerData, setValidation, t }) => {
    return (key, valueSelector = (cd) => cd[key]) => () => {
        setValidation((prev) => {
            const rule = prev[key];
            if (!rule) return prev;
            const value = valueSelector(getCustomerData());
            return {
                ...prev,
                [key]: {
                    ...rule,
                    ...computeValidation(value, rule, t.common.required_field),
                },
            };
        });
    };
};

export const makeOnValuesChangeFactory =
    ({ getCustomerData, setCustomerData, setValidation, t }) =>
        (name) =>
            (event, value) => {
                const dropdownFields = [
                    "billing_currency",
                    "countryCode",
                    "country",
                    "preferences",
                    "city",
                    "location",
                    "Industry",
                    "mode",
                    "account_type",
                    "disbursement_currency",
                ];

                const isDropdown = dropdownFields.includes(name);
                const nextValue = isDropdown ? value?.value : event?.target?.value;

                setCustomerData((prev) => ({
                    ...prev,
                    [name]: nextValue,
                    ...(name === "country" ? { city: "" } : {}),
                }));

                const key = vKeyOf(name);
                setValidation((prev) => {
                    const rule = prev[key];
                    if (!rule) return prev;
                    const res = computeValidation(nextValue, rule, t.common.required_field);
                    return { ...prev, [key]: { ...rule, ...res } };
                });
            };

export const makeValidateOnSubmitFactory =
    ({ getCustomerData, validation, setValidation, t }) =>
        () => {
            const customerData = getCustomerData();
            const requiredKeys = [
                "name",
                "location",
                "industry",
                "phone",
                "email",
                "credit_days",
                "preferences",
            ];
            const next = { ...validation };

            requiredKeys.forEach((k) => {
                const value = k === "industry" ? customerData.Industry : customerData[k];
                const res = computeValidation(value, next[k], t.common.required_field);
                next[k] = { ...next[k], ...res };
            });

            if (customerData.endDate && customerData.startDate) {
                const ok = new Date(customerData.endDate) > new Date(customerData.startDate);
                if (!ok) {
                    next.endDate = {
                        required: false,
                        invalidMessage:
                            t.vendors.form.end_after_start || "End date must be after start date",
                        pattern: () => false,
                        error: true,
                        skipped: true,
                    };
                }
            }

            setValidation(next);
            return Object.values(next).some((r) => r?.error);
        };
